<!-- SystemAdmin.vue -->
<template>
  <div class="system-admin-container">
    <!-- 搜索表单 -->
    <div class="search-card card">
      <form class="search-form">
        <div class="form-row">
          <div class="form-group">
            <label for="officialName">官网名</label>
            <input
                id="officialName"
                v-model="searchForm.officialName"
                type="text"
                placeholder="请输入官网名"
                class="form-control"
            />
          </div>
          <div class="form-group">
            <label for="account">登录账号</label>
            <input
                id="account"
                v-model="searchForm.account"
                type="text"
                placeholder="请输入登录账号"
                class="form-control"
            />
          </div>
        </div>
        <div class="form-actions">
          <button
              type="button"
              class="btn btn-primary"
              @click="handleSearch"
          >
            搜索
          </button>
          <button
              type="button"
              class="btn btn-default"
              @click="resetSearch"
          >
            重置
          </button>
        </div>
      </form>
    </div>

    <!-- 表格 -->
    <div class="table-card card">
      <div
          v-if="loading"
          class="loading-mask"
      >
        加载中...
      </div>
      <table class="data-table">
        <thead>
        <tr>
          <th>ID</th>
          <th>官网名</th>
          <th>官网地址</th>
          <th>登录账号</th>
          <th>角色类型</th>
          <th>创建时间</th>
          <th>最后登录时间</th>
          <th>操作</th>
        </tr>
        </thead>
        <tbody>
        <tr
            v-for="item in tableData"
            :key="item.id"
        >
          <td>{{ item.id }}</td>
          <td>{{ item.officialName }}</td>
          <td>{{ item.officialUrl }}</td>
          <td>{{ item.account }}</td>
          <td>{{ item.roleType === 1 ? '平台管理员' : '普通管理员' }}</td>
          <td>{{ item.createTime }}</td>
          <td>{{ item.lastLoginTime }}</td>
          <td>
            <button
                type="button"
                class="btn btn-primary btn-sm"
                @click="handleEdit(item)"
            >
              编辑
            </button>
          </td>
        </tr>
        <tr v-if="tableData.length === 0 && !loading">
          <td
              colspan="8"
              class="empty-cell"
          >
            暂无数据
          </td>
        </tr>
        </tbody>
      </table>

      <!-- 分页 -->
      <div class="pagination-container">
        <div class="pagination-info">
          共 {{ pagination.rowCount }} 条记录
        </div>
        <div class="pagination-controls">
          <button
              class="btn btn-default"
              :disabled="pagination.pageNum === 1"
              @click="handlePageChange(pagination.pageNum - 1)"
          >
            上一页
          </button>
          <span class="page-info">
            第 {{ pagination.pageNum }} 页 / 共 {{ Math.ceil(pagination.rowCount / pagination.pageSize) }} 页
          </span>
          <button
              class="btn btn-default"
              :disabled="pagination.pageNum >= Math.ceil(pagination.rowCount / pagination.pageSize)"
              @click="handlePageChange(pagination.pageNum + 1)"
          >
            下一页
          </button>
          <select
              v-model="pagination.pageSize"
              class="page-size-select"
              @change="handleSizeChange"
          >
            <option
                v-for="size in [10, 20, 50, 100]"
                :key="size"
                :value="size"
            >
              每页 {{ size }} 条
            </option>
          </select>
        </div>
      </div>
    </div>
    <!-- 在 template 末尾添加编辑弹窗 -->
    <div class="form-modal" v-if="isEditModalVisible">
      <div class="modal-overlay" @click="closeEditModal"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">编辑管理员</h3>
          <span class="close-btn" @click="closeEditModal">×</span>
        </div>
        <form @submit.prevent="saveEdit" class="modal-form">
          <div class="form-group">
            <label class="form-label">官网名</label>
            <div class="form-input">
              <input
                  type="text"
                  v-model="editForm.officialName"
                  required
                  placeholder="请输入官网名"
                  class="form-control"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">官网地址</label>
            <div class="form-input">
              <input
                  type="text"
                  v-model="editForm.officialUrl"
                  required
                  placeholder="请输入官网地址"
                  class="form-control"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">登录账号</label>
            <div class="form-input">
              <input
                  type="text"
                  v-model="editForm.account"
                  required
                  placeholder="请输入登录账号"
                  class="form-control"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">角色类型</label>
            <div class="form-input">
              <select v-model="editForm.roleType" class="form-control">
                <option :value="1">平台管理员</option>
                <option :value="2">普通管理员</option>
              </select>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-default" @click="closeEditModal">取消</button>
            <button type="submit" class="btn btn-primary">保存</button>
            <div class="error-message" v-if="errorMsg">{{ errorMsg }}</div>

          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { getSystemAdminList, updateSystemAdmin } from '@/api/website'

// 分页数据
const pagination = reactive({
  pageNum: 1,
  pageSize: 10,
  rowCount: 0
})

// 搜索表单数据
const searchForm = reactive({
  officialName: '',
  account: ''
})

// 表格数据
const tableData = ref([])
const loading = ref(false)
const editForm = reactive({
  id: null,
  officialName: '',
  officialUrl: '',
  account: '',
  roleType: 1
})
// 控制编辑弹窗显示
const isEditModalVisible = ref(false)
const errorMsg = ref('')

// 处理编辑操作
const handleEdit = (item) => {
  // 填充编辑表单数据
  Object.assign(editForm, item)
  isEditModalVisible.value = true
}
// 关闭编辑弹窗
const closeEditModal = () => {
  isEditModalVisible.value = false
}

// 保存编辑
const saveEdit = async () => {
  try {
    // 这里需要调用更新接口，假设为 updateSystemAdmin
   let response = await updateSystemAdmin(editForm)
    if (response.data && response.data.code === 200) {
      alert('编辑成功！')
      closeEditModal()
      fetchSystemAdminList() // 重新加载数据
    }else{
      errorMsg.value = response.data.message;
    }
    console.log('保存编辑:', editForm)

  } catch (error) {
    console.error('保存失败:', error)
  }
}
// 获取列表数据
const fetchSystemAdminList = async () => {
  loading.value = true
  try {
    const params = {
      pageNum: pagination.pageNum,
      pageSize: pagination.pageSize,
      ...searchForm
    }
    console.log('获取管理员列表参数:', params)
    const response = await getSystemAdminList(params)
    console.log('获取管理员列表成功:', response.data.data)
    if (response.data.code === 200){
      tableData.value = response.data.data.list
      pagination.rowCount = response.data.data.rowCount
    }
  } catch (error) {
    console.error('获取管理员列表失败:', error)
    tableData.value = []
    pagination.rowCount = 0
  } finally {
    loading.value = false
  }
}

// 搜索
const handleSearch = () => {
  pagination.pageNum = 1
  fetchSystemAdminList()
}

// 重置搜索
const resetSearch = () => {
  searchForm.officialName = ''
  searchForm.account = ''
  pagination.pageNum = 1
  fetchSystemAdminList()
}

// 分页变化
const handlePageChange = (page) => {
  pagination.pageNum = page
  fetchSystemAdminList()
}

// 每页条数变化
const handleSizeChange = () => {
  pagination.pageNum = 1
  fetchSystemAdminList()
}

// 删除管理员记录
const handleDelete = async (id) => {
  if (!confirm('确定要删除这条记录吗？')) {
    return
  }
  try {
    await deleteSystemAdmin(id)
    fetchSystemAdminList()
  } catch (error) {
    console.error('删除失败:', error)
  }
}

// 初始化加载
onMounted(() => {
  fetchSystemAdminList()
})
</script>

<style scoped lang="scss">
.system-admin-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;

  .card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    padding: 20px;
  }

  .search-card {
    .search-form {
      .form-row {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        margin-bottom: 20px;

        .form-group {
          flex: 1;
          min-width: 200px;

          label {
            display: block;
            margin-bottom: 5px;
            font-weight: 500;
            color: #333;
          }

          .form-control {
            width: 50%;
            padding: 8px 12px;
            border: 1px solid #dcdfe6;
            border-radius: 4px;
            font-size: 14px;
            transition: border-color 0.3s;

            &:focus {
              outline: none;
              border-color: #409eff;
            }
          }
        }
      }

      .form-actions {
        text-align: right;

        .btn {
          margin-left: 10px;
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.3s;

          &.btn-primary {
            background-color: #409eff;
            color: white;

            &:hover {
              background-color: #66b1ff;
            }
          }

          &.btn-default {
            background-color: #ffffff;
            color: #606266;
            border: 1px solid #dcdfe6;

            &:hover {
              background-color: #f5f7fa;
            }
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }
    }
  }

  .table-card {
    position: relative;

    .loading-mask {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 60px;
      background: rgba(255, 255, 255, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      font-size: 16px;
      color: #606266;
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 14px;

      th,
      td {
        padding: 12px 10px;
        text-align: left;
        border-bottom: 1px solid #ebeef5;
      }

      th {
        background-color: #f5f7fa;
        font-weight: 500;
        color: #606266;
      }

      tbody {
        tr {
          &:hover {
            background-color: #f5f7fa;
          }

          td {
            color: #606266;

            .btn {
              padding: 4px 8px;
              border: none;
              border-radius: 3px;
              cursor: pointer;
              font-size: 12px;
              transition: background-color 0.3s;

              &.btn-danger {
                background-color: #f56c6c;
                color: white;

                &:hover {
                  background-color: #f78989;
                }
              }

              &.btn-sm {
                padding: 3px 6px;
                font-size: 12px;
              }
            }
          }
        }

        .empty-cell {
          text-align: center;
          color: #909399;
          font-style: italic;
        }
      }
    }

    .pagination-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 20px;
      padding-top: 20px;
      border-top: 1px solid #ebeef5;

      .pagination-info {
        color: #606266;
        font-size: 14px;
      }

      .pagination-controls {
        display: flex;
        align-items: center;
        gap: 10px;

        .btn {
          padding: 6px 12px;
          border: 1px solid #dcdfe6;
          border-radius: 4px;
          background: #fff;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s;

          &:hover:not(:disabled) {
            background-color: #f5f7fa;
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }

        .page-info {
          color: #606266;
          font-size: 14px;
          margin: 0 10px;
        }

        .page-size-select {
          padding: 6px 10px;
          border: 1px solid #dcdfe6;
          border-radius: 4px;
          background: #fff;
          font-size: 14px;
        }
      }
    }
  }
}
// 在 style 部分添加弹窗样式
.form-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;

  .modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
  }

  .modal-content {
    position: relative;
    background: white;
    border-radius: 8px;
    padding: 24px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    max-height: 90vh;
    overflow-y: auto;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .modal-title {
      font-size: 18px;
      color: #333;
      font-weight: 600;
    }

    .close-btn {
      font-size: 24px;
      color: #909399;
      cursor: pointer;
      transition: color 0.2s;

      &:hover {
        color: #409eff;
      }
    }
  }

  .modal-form {
    .form-group {
      margin-bottom: 20px;

      .form-label {
        display: block;
        margin-bottom: 8px;
        font-size: 14px;
        color: #333;
      }

      .form-input {
        .form-control {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #dcdfe6;
          border-radius: 4px;
          font-size: 14px;

          &:focus {
            outline: none;
            border-color: #409eff;
          }
        }
      }
    }

    .form-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 30px;
    }
  }
}

</style>
